import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, FileText, Calendar, Clock, User, X } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { jsPDF } from "jspdf";
import SimpleThreeDotsLoader from "../components/SimpleThreeDotsLoader";
import { FaBookmark, FaRegBookmark } from "react-icons/fa";

// Helper functions for result display
const DESCRIPTIVE_TYPES = ["vsa", "sa", "la", "long answer", "short answer"];

const normalizeTextValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) {
    return value.map(normalizeTextValue).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    const nestedKeys = [
      "option_text",
      "text",
      "content",
      "value",
      "label",
      "title",
    ];
    for (const key of nestedKeys) {
      if (value[key] != null) {
        const resolved = normalizeTextValue(value[key]);
        if (resolved) return resolved;
      }
    }
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return "";
};

const convertBreakTagsToNewlines = (value) => {
  if (typeof value !== "string") {
    return value;
  }
  return value.replace(/<br\s*\/?>/gi, "\n");
};

const extractOptions = (question) => {
  const getOptionText = (option) => {
    return normalizeTextValue(option);
  };

  const normalizeOptionsCollection = (collection) => {
    if (Array.isArray(collection)) {
      return collection.map(getOptionText).filter(Boolean);
    }
    if (collection && typeof collection === "object") {
      return Object.values(collection).map(getOptionText).filter(Boolean);
    }
    return [];
  };

  const primaryOptions = normalizeOptionsCollection(question?.options);
  if (primaryOptions.length) return primaryOptions;

  const choiceOptions = normalizeOptionsCollection(question?.choices);
  if (choiceOptions.length) return choiceOptions;

  const fallbackKeys = [
    "option_a",
    "option_b",
    "option_c",
    "option_d",
    "option_e",
    "option_f",
    "answer_a",
    "answer_b",
    "answer_c",
    "answer_d",
  ];
  const fallbackOptions = fallbackKeys
    .map((key) => getOptionText(question?.[key]))
    .filter(Boolean);

  return fallbackOptions;
};

const getNormalizedQuestionType = (question) => {
  const rawType = question?.question_type;
  if (typeof rawType === "string") {
    return rawType.trim().toLowerCase();
  }
  return "";
};

const isDescriptiveQuestionType = (question) => {
  const normalizedType = getNormalizedQuestionType(question);
  return DESCRIPTIVE_TYPES.includes(normalizedType);
};

const resolveQuestionId = (question, fallbackId) =>
  question?.question_id || question?.id || question?.questionId || fallbackId;

const resolveCorrectIndex = (question) =>
  typeof question?.correct_option_index === "number"
    ? question.correct_option_index
    : question?.correctAnswer;

const formatMarks = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(1);
};

const getFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeStepBreakdown = (stepBreakdown) => {
  if (!Array.isArray(stepBreakdown)) return [];
  return stepBreakdown.map((step) => ({
    step: step?.step ?? step?.stepNumber ?? step?.number ?? "",
    solution:
      step?.solution ?? step?.feedback ?? step?.explanation ?? step?.text ?? "",
    marks: getFiniteNumber(step?.marks ?? step?.score),
    maxMarks: getFiniteNumber(step?.maxMarks ?? step?.totalMarks),
  }));
};

const normalizeDescriptiveEvaluation = (evaluation = null) => {
  if (!evaluation || typeof evaluation !== "object") {
    return null;
  }

  const stepBreakdown = normalizeStepBreakdown(evaluation?.stepBreakdown);
  const accumulatedMax = stepBreakdown.reduce((total, step) => {
    return total + (typeof step.maxMarks === "number" ? step.maxMarks : 0);
  }, 0);
  const accumulatedMarks = stepBreakdown.reduce((total, step) => {
    return total + (typeof step.marks === "number" ? step.marks : 0);
  }, 0);
  const score =
    typeof evaluation?.score === "number"
      ? evaluation.score
      : typeof evaluation?.marks === "number"
      ? evaluation.marks
      : accumulatedMarks;
  const totalMarks =
    typeof evaluation?.totalMarks === "number"
      ? evaluation.totalMarks
      : typeof evaluation?.maxMarks === "number"
      ? evaluation.maxMarks
      : accumulatedMax;
  const feedback =
    typeof evaluation?.feedback === "string"
      ? evaluation.feedback
      : evaluation?.feedback != null
      ? String(evaluation.feedback)
      : "";

  return {
    ...evaluation,
    score,
    totalMarks,
    feedback,
    stepBreakdown,
  };
};

const mathJaxConfig = {
  tex: { inlineMath: [["$", "$"]] },
};

const latexToPlainText = (text) => {
  if (typeof text !== "string") {
    return text ?? "";
  }
  const superscriptMap = {
    0: "⁰",
    1: "¹",
    2: "²",
    3: "³",
    4: "⁴",
    5: "⁵",
    6: "⁶",
    7: "⁷",
    8: "⁸",
    9: "⁹",
  };
  const subscriptMap = {
    0: "₀",
    1: "₁",
    2: "₂",
    3: "₃",
    4: "₄",
    5: "₅",
    6: "₆",
    7: "₇",
    8: "₈",
    9: "₉",
  };

  return text
    .replace(/\$([^$]+)\$/g, "$1")
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "($1/$2)")
    .replace(/\\sqrt\{([^}]+)\}/g, "sqrt($1)")
    .replace(/\\pi/g, "pi")
    .replace(/\\alpha/g, "alpha")
    .replace(/\\beta/g, "beta")
    .replace(/\\gamma/g, "gamma")
    .replace(/\\delta/g, "delta")
    .replace(/\\theta/g, "theta")
    .replace(/\\lambda/g, "lambda")
    .replace(/\\mu/g, "mu")
    .replace(/\\sigma/g, "sigma")
    .replace(/\\phi/g, "phi")
    .replace(/\\omega/g, "omega")
    .replace(/\\sum/g, "sum")
    .replace(/\\int/g, "integral")
    .replace(/\\infty/g, "infinity")
    .replace(/\\leq/g, "<=")
    .replace(/\\geq/g, ">=")
    .replace(/\\neq/g, "!=")
    .replace(/\\pm/g, "+/-")
    .replace(/\\times/g, "x")
    .replace(/\\div/g, "/")
    .replace(/\\cdot/g, "·")
    .replace(/\^(\d+)/g, (_, digits) =>
      digits
        .split("")
        .map((digit) => superscriptMap[digit] ?? digit)
        .join("")
    )
    .replace(/_([0-9]+)/g, (_, digits) =>
      digits
        .split("")
        .map((digit) => subscriptMap[digit] ?? digit)
        .join("")
    )
    .replace(/\\left\(/g, "(")
    .replace(/\\right\)/g, ")")
    .replace(/\\left\[/g, "[")
    .replace(/\\right\]/g, "]")
    .replace(/\\left\{/g, "{")
    .replace(/\\right\}/g, "}")
    .replace(/\\_/g, "_")
    .replace(/\\%/g, "%")
    .replace(/\\([a-zA-Z]+)/g, "$1")
    .replace(/\{([^}]+)\}/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
};

const sanitizeForPDF = (text) => {
  if (typeof text !== "string") {
    return text == null ? "" : String(text);
  }
  return text
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
};

const normalizeWorksheetQuestionType = (type) => {
  if (!type) return "mcq";
  const normalized =
    typeof type === "string" ? type.trim().toLowerCase() : "mcq";
  switch (normalized) {
    case "short answer":
    case "short_answer":
    case "descriptive":
    case "sa":
      return "short_answer";
    case "true/false":
    case "true_false":
    case "truefalse":
    case "tf":
      return "true_false";
    case "mcq":
    case "multiple choice":
    case "multiple_choice":
      return "mcq";
    default:
      return normalized.replace(/[^a-z0-9]+/g, "_") || "mcq";
  }
};

const getNormalizedQuestionTypeFromQuestion = (question) => {
  if (!question || typeof question !== "object") {
    return "";
  }
  const rawType =
    question.question_type ||
    question.questionType ||
    question.type ||
    (typeof question.meta === "object" ? question.meta?.question_type : "");
  if (typeof rawType === "string" && rawType.trim()) {
    return normalizeWorksheetQuestionType(rawType);
  }
  if (Array.isArray(question.options) && question.options.length > 0) {
    return "mcq";
  }
  return "";
};

const getQuestionTypeDisplayLabel = (typeKey = "") => {
  switch (typeKey) {
    case "mcq":
      return "Multiple Choice";
    case "short_answer":
      return "Descriptive";
    case "true_false":
      return "True / False";
    default:
      if (!typeKey) return "";
      return typeKey
        .split("_")
        .map((segment) =>
          segment.length > 0
            ? segment[0].toUpperCase() + segment.slice(1)
            : segment
        )
        .join(" ");
  }
};

const summarizeWorksheetQuestionTypes = (questions) => {
  if (!Array.isArray(questions) || !questions.length) {
    return "";
  }
  const uniqueTypes = Array.from(
    new Set(
      questions
        .map((question) => getNormalizedQuestionTypeFromQuestion(question))
        .filter(Boolean)
    )
  );
  if (!uniqueTypes.length) {
    return "";
  }
  if (uniqueTypes.length === 1) {
    return getQuestionTypeDisplayLabel(uniqueTypes[0]);
  }
  return "Mixed Question Types";
};

const isMCQQuestion = (question) =>
  getNormalizedQuestionTypeFromQuestion(question) === "mcq";
const isTrueFalseQuestion = (question) =>
  getNormalizedQuestionTypeFromQuestion(question) === "true_false";

const getOptionLabel = (index) => {
  if (!Number.isInteger(index) || index < 0) {
    return "";
  }
  let label = "";
  let current = index;
  do {
    label = String.fromCharCode(65 + (current % 26)) + label;
    current = Math.floor(current / 26) - 1;
  } while (current >= 0);
  return label;
};

const generateWorksheetPdf = ({
  subject,
  topic,
  questions,
  questionCount,
  timerMinutes,
  worksheetTypeLabel,
  attemptId,
}) => {
  if (!Array.isArray(questions) || !questions.length) {
    return;
  }

  const pdf = new jsPDF();
  pdf.setFont("helvetica", "normal");

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPosition = 20;
  const lineHeight = 7;
  const margin = 20;

  const addWatermark = () => {
    pdf.saveGraphicsState();
    pdf.setFontSize(48);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(200, 200, 200);
    const centerX = pageWidth / 2;
    const centerY = pageHeight / 2;
    pdf.text("Aanya", centerX, centerY, {
      align: "center",
      angle: 45,
    });
    pdf.restoreGraphicsState();
  };

  const addText = (text, fontSize = 12, isBold = false) => {
    pdf.setFontSize(fontSize);
    pdf.setFont("helvetica", isBold ? "bold" : "normal");
    const lines = pdf.splitTextToSize(text, pageWidth - 2 * margin);
    pdf.text(lines, margin, yPosition);
    yPosition += lines.length * lineHeight + 5;
    if (yPosition > pageHeight - 20) {
      pdf.addPage();
      yPosition = 20;
      addWatermark();
    }
  };

  const safeSubject = subject || "Worksheet";
  const safeTopic = topic || "Topic";
  const safeQuestionCount = questionCount || questions.length;
  const safeTimerMinutes = Math.max(1, Math.round(timerMinutes || 1));
  const safeTypeLabel = worksheetTypeLabel || "Worksheet";

  pdf.setFontSize(20);
  pdf.setFont("helvetica", "bold");
  pdf.text(`${safeSubject} Worksheet`, pageWidth / 2, yPosition, {
    align: "center",
  });
  yPosition += 15;

  pdf.setFontSize(16);
  pdf.setFont("helvetica", "normal");
  pdf.text(`Topic: ${safeTopic}`, pageWidth / 2, yPosition, {
    align: "center",
  });
  yPosition += 10;

  pdf.setFontSize(12);
  pdf.text(
    `Questions: ${safeQuestionCount} • Type: ${safeTypeLabel} • Duration: ${safeTimerMinutes} mins • Difficulty: Intermediate`,
    pageWidth / 2,
    yPosition,
    { align: "center" }
  );
  yPosition += 20;

  addWatermark();

  const getQuestionDifficulty = (question, index) => {
    const difficulty =
      question?.difficulty ||
      question?.level ||
      (index < questions.length / 3
        ? "easy"
        : index < (questions.length * 2) / 3
        ? "medium"
        : "hard");
    return String(difficulty || "medium").toLowerCase();
  };

  const getQuestionMarks = (question, difficulty) => {
    const marksMap = { easy: 1, medium: 2, hard: 3 };
    return question?.marks || marksMap[difficulty] || 2;
  };

  const getRoughWorkHeight = (difficulty) => {
    const heightMap = { easy: 3, medium: 6, hard: 9 };
    return heightMap[difficulty] || 6;
  };

  const calculateQuestionSpace = (question, index) => {
    const difficulty = getQuestionDifficulty(question, index);
    const roughWorkHeight = getRoughWorkHeight(difficulty);

    const questionText =
      question?.questionText ||
      question?.question_text ||
      question?.question ||
      `Question ${index + 1}`;
    const plainText = latexToPlainText(questionText);
    const sanitizedText = sanitizeForPDF(plainText);

    const leftColumnWidth = pageWidth * 0.7;
    const questionLines = pdf.splitTextToSize(
      `${index + 1}. ${sanitizedText}`,
      leftColumnWidth - margin
    );
    const questionHeight = questionLines.length * lineHeight + 10;

    let optionsHeight = 0;
    const mcqQuestion = isMCQQuestion(question);
    if (mcqQuestion && question.options && question.options.length > 0) {
      question.options.forEach((option, optionIndex) => {
        const optionText =
          typeof option === "string"
            ? option
            : option.option_text || option.text || option;
        const plainOptionText = latexToPlainText(optionText);
        const sanitizedOptionText = sanitizeForPDF(plainOptionText);
        const optionLabel = getOptionLabel(optionIndex);
        const optionLines = pdf.splitTextToSize(
          `   ${optionLabel ? `${optionLabel}. ` : ""}${sanitizedOptionText}`,
          pageWidth - 2 * margin
        );
        optionsHeight += optionLines.length * lineHeight + 2;
      });
    } else if (isTrueFalseQuestion(question)) {
      optionsHeight = 2 * lineHeight + 4;
    }

    const roughWorkBoxHeight = roughWorkHeight * lineHeight;
    const roughWorkHeaderHeight = 15;

    return (
      questionHeight +
      optionsHeight +
      roughWorkHeaderHeight +
      roughWorkBoxHeight +
      20
    );
  };

  questions.forEach((question, index) => {
    const difficulty = getQuestionDifficulty(question, index);
    const marks = getQuestionMarks(question, difficulty);
    const roughWorkHeight = getRoughWorkHeight(difficulty);
    const mcqQuestion = isMCQQuestion(question);
    const trueFalseQuestion = isTrueFalseQuestion(question);

    const requiredSpace = calculateQuestionSpace(question, index);
    if (yPosition + requiredSpace > pageHeight - 20) {
      pdf.addPage();
      yPosition = 20;
      addWatermark();
    }

    const questionText =
      question?.questionText ||
      question?.question_text ||
      question?.question ||
      `Question ${index + 1}`;
    const plainText = latexToPlainText(questionText);
    const sanitizedText = sanitizeForPDF(plainText);

    const leftColumnWidth = pageWidth * 0.7;
    const rightColumnStart = pageWidth * 0.72;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");
    const questionLines = pdf.splitTextToSize(
      `${index + 1}. ${sanitizedText}`,
      leftColumnWidth - margin
    );
    pdf.text(questionLines, margin, yPosition);

    const answerBoxY = yPosition - 5;
    const answerBoxSize = 20;
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.rect(rightColumnStart, answerBoxY, answerBoxSize, answerBoxSize);

    const marksText = `[${marks} mark${marks > 1 ? "s" : ""}]`;
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "italic");
    pdf.text(marksText, rightColumnStart, answerBoxY + answerBoxSize + 8);

    yPosition += Math.max(
      questionLines.length * lineHeight + 10,
      answerBoxSize + 10
    );

    if (mcqQuestion && question.options && question.options.length > 0) {
      question.options.forEach((option, optionIndex) => {
        const optionText =
          typeof option === "string"
            ? option
            : option.option_text || option.text || option;
        const plainOptionText = latexToPlainText(optionText);
        const sanitizedOptionText = sanitizeForPDF(plainOptionText);
        const optionLabel = getOptionLabel(optionIndex);
        addText(
          `   ${optionLabel ? `${optionLabel}. ` : ""}${sanitizedOptionText}`,
          10,
          false
        );
      });
    } else if (trueFalseQuestion) {
      addText("   A. True", 10, false);
      addText("   B. False", 10, false);
    }

    yPosition += 5;
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("Calculations / Step-by-step Solution:", margin, yPosition);
    yPosition += 5;

    const roughWorkBoxHeight = roughWorkHeight * lineHeight;
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.rect(margin, yPosition, pageWidth - 2 * margin, roughWorkBoxHeight);
    yPosition += roughWorkBoxHeight + 15;
  });

  yPosition = pageHeight - 20;
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");
  pdf.text(
    `Generated on ${new Date().toLocaleDateString()}`,
    pageWidth / 2,
    yPosition,
    { align: "center" }
  );

  const safeAttemptId = attemptId ? `_${attemptId}` : "";
  const fileName = `${safeSubject}_${safeTopic}_Worksheet${safeAttemptId}_${
    new Date().toISOString().split("T")[0]
  }.pdf`;
  pdf.save(fileName);
};

const GeneratedWorksheets = () => {
  const [worksheets, setWorksheets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const preservedSearch = location.search || "";
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const limit = 20;

  // Result modal state
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [isFetchingResults, setIsFetchingResults] = useState(false);
  const [isEvaluatingScore, setIsEvaluatingScore] = useState(false);
  const [currentAttemptId, setCurrentAttemptId] = useState(null);
  const retryIntervalRef = useRef(null);
  const [savedQuestions, setSavedQuestions] = useState(new Set());
  const [savingQuestionIds, setSavingQuestionIds] = useState(new Set());

  // Transform API data to worksheet format
  const transformAttemptData = (attempt) => {
    const level = attempt.level || "intermediate";
    const name = attempt.chapter ? `${attempt.chapter} Worksheet` : "Worksheet";
    const timerSeconds = attempt.timer || attempt.totalQuestions * 180;
    const timerMinutes = Math.round(timerSeconds / 60);

    return {
      id: attempt.attempt_id,
      title: name,
      subject: attempt.subject || "Unknown",
      topic: attempt.chapter || "Unknown",
      questionCount: attempt.totalQuestions || 0,
      difficulty: level.charAt(0).toUpperCase() + level.slice(1),
      createdAt: attempt.generatedAt || attempt.createdAt,
      timerMinutes: timerMinutes,
      status: attempt.status || "active",
      questionType: attempt.questionType || "MCQ",
      subType: attempt.subType || "",
    };
  };

  // Fetch worksheets from API
  const fetchWorksheets = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Please sign in to view worksheets");
          setIsLoading(false);
          return;
        }

        if (!baseUrl) {
          console.error("VITE_APP_BASE_URL is not configured");
          toast.error("API configuration error");
          setIsLoading(false);
          return;
        }

        const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });

        const response = await axios.get(
          `${trimmedBaseUrl}/v2/quiz/practice-quiz/attempts?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = response.data || {};
        const attempts = Array.isArray(data.attempts) ? data.attempts : [];
        const transformed = attempts.map(transformAttemptData);

        setWorksheets(transformed);
        setTotalAttempts(data.total_attempts || attempts.length);

        // Calculate total pages
        const total = data.total_attempts || attempts.length;
        const calculatedPages = Math.ceil(total / limit);
        setTotalPages(calculatedPages > 0 ? calculatedPages : 1);
      } catch (error) {
        console.error("Error fetching worksheets:", error);
        toast.error(
          error.response?.data?.message ||
            "Failed to load worksheets. Please try again."
        );
        setWorksheets([]);
      } finally {
        setIsLoading(false);
      }
    },
    [baseUrl, limit]
  );

  useEffect(() => {
    fetchWorksheets(currentPage);
  }, [currentPage, fetchWorksheets]);

  const handleBackToWorksheets = useCallback(() => {
    const historyObj =
      typeof window !== "undefined" ? window.history : undefined;
    const hasHistory =
      typeof historyObj?.state?.idx === "number"
        ? historyObj.state.idx > 0
        : (historyObj?.length || 0) > 1;

    if (hasHistory) {
      navigate(-1);
      return;
    }

    navigate(
      {
        pathname: "/practice/sampleWorksheet",
        search: preservedSearch,
      },
      { replace: true }
    );
  }, [navigate, preservedSearch]);

  const handleDownload = async (worksheet) => {
    if (!worksheet) {
      toast.error("Worksheet information is missing.");
      return;
    }

    const attemptId =
      worksheet?.attempt_id || worksheet?.attemptId || worksheet?.id;
    if (!attemptId) {
      toast.error("Unable to determine worksheet attempt.");
      return;
    }

    if (!baseUrl) {
      toast.error("API configuration error.");
      return;
    }

    const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
    const requestUrl = `${trimmedBaseUrl}/v2/quiz/get-questions/${attemptId}`;

    try {
      const token = localStorage.getItem("token");
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      console.log("GeneratedWorksheets | Fetching worksheet questions", {
        attemptId,
        url: requestUrl,
      });

      const response = await fetch(requestUrl, {
        method: "GET",
        headers,
      });

      const responseText = await response.text();
      console.log("GeneratedWorksheets | Worksheet question response", {
        attemptId,
        status: response.status,
        ok: response.ok,
        body: responseText,
      });

      if (!response.ok) {
        throw new Error(
          responseText ||
            `Failed to fetch worksheet questions (status ${response.status}).`
        );
      }

      let parsedData = {};
      if (responseText) {
        try {
          parsedData = JSON.parse(responseText);
        } catch (parseError) {
          console.error(
            "GeneratedWorksheets | Failed to parse worksheet response",
            parseError
          );
          throw new Error("Received invalid worksheet response.");
        }
      }

      const worksheetPayload =
        parsedData?.result && typeof parsedData.result === "object"
          ? parsedData.result
          : parsedData;
      const worksheetQuestions = Array.isArray(worksheetPayload?.questions)
        ? worksheetPayload.questions
        : [];

      if (!worksheetQuestions.length) {
        throw new Error("No Questions found.");
      }

      const resolvedQuestionCount =
        typeof worksheetPayload?.total_questions === "number" &&
        Number.isFinite(worksheetPayload.total_questions)
          ? worksheetPayload.total_questions
          : worksheet.questionCount || worksheetQuestions.length;

      const worksheetTypeLabel =
        summarizeWorksheetQuestionTypes(worksheetQuestions) ||
        getQuestionTypeDisplayLabel(
          normalizeWorksheetQuestionType(worksheet?.questionType)
        );

      const payloadTimerSeconds =
        typeof worksheetPayload?.timer === "number" &&
        Number.isFinite(worksheetPayload.timer)
          ? worksheetPayload.timer
          : typeof worksheetPayload?.duration === "number" &&
            Number.isFinite(worksheetPayload.duration)
          ? worksheetPayload.duration
          : null;

      const resolvedTimerMinutes =
        typeof worksheet?.timerMinutes === "number" &&
        Number.isFinite(worksheet.timerMinutes) &&
        worksheet.timerMinutes > 0
          ? worksheet.timerMinutes
          : payloadTimerSeconds && payloadTimerSeconds > 0
          ? Math.max(1, Math.round(payloadTimerSeconds / 60))
          : Math.max(1, Math.round(resolvedQuestionCount));

      generateWorksheetPdf({
        subject: worksheet.subject,
        topic: worksheet.topic,
        questions: worksheetQuestions,
        questionCount: resolvedQuestionCount,
        timerMinutes: resolvedTimerMinutes,
        worksheetTypeLabel,
        attemptId,
      });

      toast.success("Worksheet PDF downloaded successfully.");
    } catch (error) {
      console.error("Failed to fetch worksheet questions:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to fetch worksheet questions."
      );
    }
  };

  const handleTakeQuiz = (worksheet) => {
    // Navigate to quiz with worksheet data
    navigate(`/practice/quiz?attemptId=${worksheet.id}`);
    toast.info("Starting worksheet quiz...");
  };

  const fetchQuizResults = async (attemptId, retryCount = 0) => {
    setIsFetchingResults(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token is missing.");
      }

      const baseResultUrl = `${baseUrl}/v2/quiz/get-result/${attemptId}`;
      const resultUrl =
        retryCount > 0 ? `${baseResultUrl}?retry=${retryCount}` : baseResultUrl;

      const response = await fetch(resultUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText
            ? `Failed to fetch quiz results: ${errorText}`
            : `Failed to fetch quiz results (status ${response.status}).`
        );
      }

      const parsedData = await response.json();
      const normalizedResult =
        parsedData?.result && typeof parsedData.result === "object"
          ? parsedData.result
          : parsedData;

      // Check if evaluation is still in progress
      const rawResultStatus = normalizedResult?.status;
      const normalizedResultStatus =
        typeof rawResultStatus === "string"
          ? rawResultStatus.trim().toUpperCase()
          : "";
      const shouldRetryForStatus =
        normalizedResultStatus === "NOT_EVALUATED" ||
        normalizedResultStatus === "ERROR";
      if (shouldRetryForStatus) {
        setIsEvaluatingScore(true);
        setIsFetchingResults(false);

        // Clear any existing retry interval
        if (retryIntervalRef.current) {
          clearTimeout(retryIntervalRef.current);
        }

        // Retry after 10 seconds with incremented retry count
        retryIntervalRef.current = setTimeout(() => {
          fetchQuizResults(attemptId, retryCount + 1);
        }, 10000);

        return; // Don't process results yet
      }

      // Evaluation is complete, stop retrying
      setIsEvaluatingScore(false);
      if (retryIntervalRef.current) {
        clearTimeout(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }

      // Only proceed if status is "Completed"
      if (normalizedResultStatus !== "COMPLETED") {
        throw new Error(`Unexpected result status: ${rawResultStatus}`);
      }

      const normalizedQuestions = Array.isArray(normalizedResult?.questions)
        ? normalizedResult.questions.map((question) => {
            const normalizedEvaluation = normalizeDescriptiveEvaluation(
              question?.descriptiveEvaluation ?? question?.evaluation
            );
            return {
              ...question,
              _normalizedOptions: extractOptions(question),
              ...(normalizedEvaluation
                ? { descriptiveEvaluation: normalizedEvaluation }
                : {}),
            };
          })
        : [];

      setResultData({
        questions: normalizedQuestions,
        totalQuestions:
          normalizedResult?.total_questions || normalizedQuestions.length || 0,
        totalCorrect: normalizedQuestions.reduce((count, question) => {
          if (question?.is_correct) {
            return count + 1;
          }
          return count;
        }, 0),
      });
    } catch (error) {
      console.error("Failed to fetch quiz results:", error);
      setIsEvaluatingScore(false);
      if (retryIntervalRef.current) {
        clearTimeout(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
      toast.error(
        error.message || "Failed to load quiz results. Please try again."
      );
    } finally {
      setIsFetchingResults(false);
    }
  };

  const handleViewResult = (worksheet) => {
    setCurrentAttemptId(worksheet.id);
    setShowResultModal(true);
    setResultData(null);
    setIsEvaluatingScore(false);
    fetchQuizResults(worksheet.id);
  };

  const handleCloseResultModal = () => {
    setShowResultModal(false);
    setResultData(null);
    setCurrentAttemptId(null);
    setIsEvaluatingScore(false);
    if (retryIntervalRef.current) {
      clearTimeout(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
  };

  const handleSaveQuestion = async (question, questionIndex = 0) => {
    if (!question) {
      return;
    }

    const fallbackIndex =
      typeof questionIndex === "number" && Number.isFinite(questionIndex)
        ? questionIndex
        : 0;
    const questionId = resolveQuestionId(
      question,
      `question-${fallbackIndex + 1}`
    );

    if (!questionId) {
      return;
    }

    if (savedQuestions.has(questionId) || savingQuestionIds.has(questionId)) {
      return;
    }

    setSavingQuestionIds((prev) => {
      const next = new Set(prev);
      next.add(questionId);
      return next;
    });

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please sign in to save questions");
        return;
      }

      if (!baseUrl) {
        toast.error("API configuration error");
        return;
      }

      const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
      const payload = {
        question_id: questionId,
        attempt_id:
          question?.attempt_id ||
          question?.attemptId ||
          currentAttemptId ||
          resultData?.attempt_id ||
          "",
        subject:
          question?.subject ||
          resultData?.subject ||
          question?.topic ||
          question?.chapter ||
          "",
        chapter:
          question?.chapter ||
          question?.topic ||
          question?.subtopic ||
          resultData?.chapter ||
          "",
      };

      const response = await axios.post(
        `${trimmedBaseUrl}/v2/quiz/mark-question`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { success, message } = response?.data || {};
      if (success === false) {
        toast.error(message || "Failed to save question");
        return;
      }

      setSavedQuestions((prev) => {
        const next = new Set(prev);
        next.add(questionId);
        return next;
      });
      toast.success(message || "Saved for revision");
    } catch (error) {
      console.error("Failed to save question:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to save question. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSavingQuestionIds((prev) => {
        const next = new Set(prev);
        next.delete(questionId);
        return next;
      });
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryIntervalRef.current) {
        clearTimeout(retryIntervalRef.current);
      }
    };
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDifficultyColor = (difficulty) => {
    const normalized = difficulty.toLowerCase();
    switch (normalized) {
      case "beginner":
      case "easy":
        return "bg-green-100 text-green-800";
      case "intermediate":
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "advanced":
      case "hard":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatTimer = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours} hour${hours !== 1 ? "s" : ""}`;
    }
    return `${hours} hour${hours !== 1 ? "s" : ""} ${mins} minute${
      mins !== 1 ? "s" : ""
    }`;
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white z-50 overflow-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4">
          <button
            onClick={handleBackToWorksheets}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium">Back to Worksheets</span>
          </button>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D99FF]"></div>
            <p className="text-gray-600 font-medium">Loading worksheets...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-auto">
      {/* Header with back button */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4">
        <button
          onClick={handleBackToWorksheets}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="text-sm font-medium">Back to Worksheets</span>
        </button>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center sm:items-start mb-6 gap-4 sm:gap-6">
          <div className="bg-[#0D99FF] rounded-full p-3">
            <div className="bg-white rounded-full p-1">
              <div className="bg-[#0D99FF] rounded-full w-8 h-8 flex items-center justify-center">
                <span className="text-white text-xs sm:text-base">📄</span>
              </div>
            </div>
          </div>
          <div className="text-center sm:text-left">
            <h2 className="text-gray-700 text-lg font-medium">
              Generated Worksheets
            </h2>
            <p className="text-gray-500 text-sm">
              View and download your previously generated worksheets
            </p>
          </div>
        </div>

        {/* Worksheets List */}
        <div className="space-y-4">
          {worksheets.length === 0 && !isLoading ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No worksheets generated yet
              </h3>
              <p className="text-gray-500">
                Generate your first worksheet to see it here
              </p>
            </div>
          ) : (
            worksheets.map((worksheet) => (
              <div
                key={worksheet.id}
                className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {worksheet.title}
                      </h3>
                      {/* <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(
                          worksheet.difficulty
                        )}`}
                      >
                        {worksheet.difficulty}
                      </span> */}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>
                          {worksheet.subject} - {worksheet.topic}
                        </span>
                      </div>
                      {/* <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span>{worksheet.questionCount} questions</span>
                      </div> */}
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{formatTimer(worksheet.timerMinutes)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(worksheet.createdAt)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {worksheet.status === "complete" && (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                          Complete
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    {worksheet.status === "complete" && (
                      <button
                        onClick={() => handleViewResult(worksheet)}
                        className="px-4 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-[#0D99FF]/90 transition-colors text-sm font-medium"
                      >
                        View Result
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(worksheet)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Worksheet
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === page
                        ? "bg-[#0D99FF] text-white"
                        : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Pagination Summary */}
        {totalAttempts > 0 && (
          <div className="mt-4 text-center text-sm text-gray-600">
            Showing {Math.min((currentPage - 1) * limit + 1, totalAttempts)} to{" "}
            {Math.min(currentPage * limit, totalAttempts)} of {totalAttempts}{" "}
            worksheets
          </div>
        )}
      </div>

      {/* Result Modal */}
      {showResultModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full h-[98vh] overflow-y-auto relative">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-800">
                Quiz Results
              </h2>
              <button
                onClick={handleCloseResultModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {isEvaluatingScore ? (
                <div className="flex flex-col justify-center items-center py-10">
                  <div className="w-full max-w-md mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Evaluating your score...
                      </span>
                      <span className="text-xs text-gray-500">Please wait</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden relative">
                      <div
                        className="h-full bg-blue-600 rounded-full"
                        style={{
                          width: "100%",
                          background:
                            "linear-gradient(90deg, #2563eb 0%, #3b82f6 50%, #2563eb 100%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 2s infinite linear",
                        }}
                      ></div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    This may take a few moments
                  </p>
                  <style>{`
                    @keyframes shimmer {
                      0% { background-position: 200% 0; }
                      100% { background-position: -200% 0; }
                    }
                  `}</style>
                </div>
              ) : isFetchingResults ? (
                <div className="flex justify-center items-center py-10">
                  <SimpleThreeDotsLoader message="Fetching detailed results..." />
                </div>
              ) : resultData ? (
                <MathJaxContext config={mathJaxConfig}>
                  <div>
                    {/* Score Summary */}
                    <div className="text-center mb-8">
                      <div className="text-5xl font-bold text-blue-600 mb-2">
                        {resultData.totalQuestions > 0
                          ? Math.round(
                              (resultData.totalCorrect /
                                resultData.totalQuestions) *
                                100
                            )
                          : 0}
                        %
                      </div>
                      <p className="text-gray-600">
                        You got {resultData.totalCorrect} out of{" "}
                        {resultData.totalQuestions} questions correct
                      </p>
                    </div>

                    {/* Question Summary */}
                    <div className="mt-8 bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-8">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        Question Summary
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-green-100 text-green-700">
                          <span className="font-medium">Correct</span>
                          <span className="text-xl font-bold">
                            {resultData.totalCorrect}
                          </span>
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-red-100 text-red-600">
                          <span className="font-medium">Incorrect</span>
                          <span className="text-xl font-bold">
                            {resultData.questions.reduce((count, q) => {
                              if (q?.is_correct === false) return count + 1;
                              return count;
                            }, 0)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-orange-100 text-orange-600">
                          <span className="font-medium">Not Attempted</span>
                          <span className="text-xl font-bold">
                            {resultData.questions.reduce((count, q) => {
                              if (
                                typeof q?.user_answer === "undefined" &&
                                typeof q?.option_index !== "number"
                              )
                                return count + 1;
                              return count;
                            }, 0)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Review Answers */}
                    {resultData.questions.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                          Review Your Answers
                        </h3>
                        <div className="space-y-4">
                          {resultData.questions.map((question, index) => {
                            const questionId = resolveQuestionId(
                              question,
                              `question-${index + 1}`
                            );
                            const isCurrentQuestionSaved =
                              savedQuestions.has(questionId);
                            const isSavingCurrentQuestion =
                              savingQuestionIds.has(questionId);
                            const isDescriptiveReviewQuestion =
                              isDescriptiveQuestionType(question);
                            const descriptiveEvaluationForReview =
                              isDescriptiveReviewQuestion &&
                              question?.descriptiveEvaluation
                                ? question.descriptiveEvaluation
                                : isDescriptiveReviewQuestion
                                ? normalizeDescriptiveEvaluation(
                                    question?.evaluation
                                  )
                                : null;
                            const descriptiveStepBreakdown = Array.isArray(
                              descriptiveEvaluationForReview?.stepBreakdown
                            )
                              ? descriptiveEvaluationForReview.stepBreakdown
                              : [];
                            const descriptiveScoreValue =
                              isDescriptiveReviewQuestion
                                ? getFiniteNumber(question?.score) ??
                                  getFiniteNumber(
                                    descriptiveEvaluationForReview?.score
                                  )
                                : null;
                            const descriptiveTotalMarksValue =
                              isDescriptiveReviewQuestion
                                ? getFiniteNumber(question?.totalMarks) ??
                                  getFiniteNumber(
                                    descriptiveEvaluationForReview?.totalMarks
                                  )
                                : null;
                            const descriptiveFeedbackText =
                              isDescriptiveReviewQuestion
                                ? convertBreakTagsToNewlines(
                                    normalizeTextValue(
                                      descriptiveEvaluationForReview?.feedback ??
                                        question?.feedback ??
                                        ""
                                    )
                                  )
                                : "";
                            const questionPrompt = normalizeTextValue(
                              question?.question_text ??
                                question?.questionText ??
                                question?.question ??
                                question?.prompt ??
                                ""
                            );
                            const optionsForQuestion =
                              question?._normalizedOptions ??
                              extractOptions(question);
                            const userSelectionIndex =
                              typeof question?.option_index === "number"
                                ? question.option_index
                                : null;
                            const correctIndex =
                              typeof question?.correct_option_index === "number"
                                ? question.correct_option_index
                                : resolveCorrectIndex(question);
                            const userSelectionText = question?.user_answer
                              ? normalizeTextValue(question.user_answer)
                              : userSelectionIndex !== null &&
                                userSelectionIndex >= 0 &&
                                userSelectionIndex < optionsForQuestion.length
                              ? optionsForQuestion[userSelectionIndex]
                              : "";
                            const correctOptionText = question?.correct_answer
                              ? normalizeTextValue(question.correct_answer)
                              : typeof correctIndex === "number" &&
                                correctIndex >= 0 &&
                                correctIndex < optionsForQuestion.length
                              ? optionsForQuestion[correctIndex]
                              : "";
                            const explanation = convertBreakTagsToNewlines(
                              normalizeTextValue(
                                question?.check_answer_text ??
                                  question?.resoning ??
                                  question?.reasoning ??
                                  question?.explanation ??
                                  question?.explanation_text ??
                                  ""
                              )
                            );
                            const notAttempted =
                              typeof question?.user_answer === "undefined" &&
                              typeof userSelectionIndex !== "number";
                            const isCorrect =
                              typeof question?.is_correct === "boolean"
                                ? question.is_correct
                                : !notAttempted &&
                                  typeof correctIndex === "number" &&
                                  userSelectionIndex === correctIndex;

                            const containerClasses = isCorrect
                              ? "bg-green-50 border border-green-200"
                              : "bg-red-50 border border-red-200";
                            const statusLabel = notAttempted
                              ? "Not answered"
                              : isCorrect
                              ? "✓ Correct!"
                              : "✗ Incorrect.";

                            // Marks handling
                            const rawMaxMarks =
                              question?.marks ??
                              question?.max_marks ??
                              question?.total_marks ??
                              question?.score_per_question ??
                              1;
                            const baseMaxMarks = Number.isFinite(
                              Number(rawMaxMarks)
                            )
                              ? Number(rawMaxMarks)
                              : 1;

                            const rawObtainedMarks =
                              question?.obtained_marks ?? question?.score;
                            const baseObtainedMarks = Number.isFinite(
                              Number(rawObtainedMarks)
                            )
                              ? Number(rawObtainedMarks)
                              : isCorrect
                              ? baseMaxMarks
                              : 0;

                            const displayedMarksValue =
                              isDescriptiveReviewQuestion &&
                              descriptiveScoreValue !== null
                                ? descriptiveScoreValue
                                : baseObtainedMarks;
                            const displayedMaxMarksValue =
                              isDescriptiveReviewQuestion &&
                              descriptiveTotalMarksValue !== null
                                ? descriptiveTotalMarksValue
                                : baseMaxMarks;
                            const displayedMarks =
                              formatMarks(displayedMarksValue) ??
                              (typeof displayedMarksValue === "number" &&
                              Number.isFinite(displayedMarksValue)
                                ? displayedMarksValue.toString()
                                : "0");
                            const displayedMaxMarks =
                              formatMarks(displayedMaxMarksValue) ??
                              (typeof displayedMaxMarksValue === "number" &&
                              Number.isFinite(displayedMaxMarksValue)
                                ? displayedMaxMarksValue.toString()
                                : "0");

                            return (
                              <div
                                key={questionId || `question-review-${index}`}
                                className={`rounded-2xl p-4 ${containerClasses}`}
                              >
                                <div className="flex flex-wrap gap-4 justify-between">
                                  <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white text-gray-800 flex items-center justify-center font-semibold border border-gray-200">
                                      {index + 1}
                                    </div>
                                    <div className="flex-1">
                                      {questionPrompt && (
                                        <div className="text-base font-semibold mb-2 whitespace-pre-line leading-relaxed">
                                          <MathJax dynamic>
                                            <div>{questionPrompt}</div>
                                          </MathJax>
                                        </div>
                                      )}
                                      <div className="font-semibold mb-1 text-sm flex items-center gap-2">
                                        <span>{statusLabel}</span>
                                        <span className="text-xs text-gray-600">
                                          Marks: {displayedMarks} /{" "}
                                          {displayedMaxMarks}
                                        </span>
                                      </div>
                                      <div className="text-sm text-gray-800 space-y-2">
                                        <div className="mt-2">
                                          <span className="font-semibold">
                                            Your answer:
                                          </span>{" "}
                                          {notAttempted ? (
                                            <span className="text-red-500">
                                              Not answered
                                            </span>
                                          ) : (
                                            <div className="mt-1 whitespace-pre-line leading-relaxed">
                                              <MathJax dynamic>
                                                <div>
                                                  {normalizeTextValue(
                                                    userSelectionText
                                                  ) || ""}
                                                </div>
                                              </MathJax>
                                            </div>
                                          )}
                                        </div>
                                        {!isDescriptiveReviewQuestion &&
                                          !isCorrect &&
                                          correctOptionText && (
                                            <div className="mt-3">
                                              <span className="font-semibold">
                                                Correct answer:
                                              </span>
                                              <div className="mt-1 whitespace-pre-line leading-relaxed">
                                                <MathJax dynamic>
                                                  <div>{correctOptionText}</div>
                                                </MathJax>
                                              </div>
                                            </div>
                                          )}
                                        {!isDescriptiveReviewQuestion &&
                                          explanation && (
                                            <div className="mt-3">
                                              <span className="font-semibold">
                                                Explanation:
                                              </span>
                                              <div className="mt-1 whitespace-pre-line leading-relaxed">
                                                <MathJax dynamic>
                                                  <div>{explanation}</div>
                                                </MathJax>
                                              </div>
                                            </div>
                                          )}
                                        {isDescriptiveReviewQuestion &&
                                          descriptiveFeedbackText && (
                                            <div className="mt-3">
                                              <span className="font-semibold">
                                                Feedback:
                                              </span>
                                              <div className="mt-1 whitespace-pre-line leading-relaxed">
                                                <MathJax dynamic>
                                                  <div>
                                                    {descriptiveFeedbackText}
                                                  </div>
                                                </MathJax>
                                              </div>
                                            </div>
                                          )}
                                        {isDescriptiveReviewQuestion &&
                                          !descriptiveFeedbackText &&
                                          explanation && (
                                            <div className="mt-3">
                                              <span className="font-semibold">
                                                Explanation:
                                              </span>
                                              <div className="mt-1 whitespace-pre-line leading-relaxed">
                                                <MathJax dynamic>
                                                  <div>{explanation}</div>
                                                </MathJax>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-start">
                                    <button
                                      onClick={() =>
                                        handleSaveQuestion(question, index)
                                      }
                                      disabled={
                                        isSavingCurrentQuestion ||
                                        isCurrentQuestionSaved
                                      }
                                      className={`p-2 rounded-full flex items-center justify-center transition-all ${
                                        isSavingCurrentQuestion
                                          ? "opacity-50 cursor-not-allowed text-blue-400 bg-blue-50"
                                          : isCurrentQuestionSaved
                                          ? "bg-blue-100 text-blue-700 border border-blue-300 cursor-default shadow-sm"
                                          : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      }`}
                                      title={
                                        isCurrentQuestionSaved
                                          ? "Saved for revision"
                                          : "Save for revision"
                                      }
                                    >
                                      {isCurrentQuestionSaved ? (
                                        <FaBookmark
                                          size={16}
                                          className="text-blue-700"
                                        />
                                      ) : (
                                        <FaRegBookmark size={16} />
                                      )}
                                    </button>
                                  </div>
                                </div>
                                {isDescriptiveReviewQuestion &&
                                  descriptiveStepBreakdown.length > 0 && (
                                    <div className="mt-4">
                                      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                                        <div className="px-4 py-3 border-b border-gray-100">
                                          <p
                                            style={{
                                              paddingBottom: "0px",
                                              marginBottom: "0px",
                                            }}
                                            className="text-sm font-semibold text-gray-800"
                                          >
                                            Step-by-step evaluation
                                          </p>
                                        </div>
                                        <div className="overflow-x-auto">
                                          <table className="w-full text-sm">
                                            <thead>
                                              <tr className="bg-gray-50 text-gray-600">
                                                <th className="py-2 px-3 text-left w-20">
                                                  Step
                                                </th>
                                                <th className="py-2 px-3 text-left">
                                                  Solution
                                                </th>
                                                <th className="py-2 px-3 text-right whitespace-nowrap">
                                                  Marks
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {descriptiveStepBreakdown.map(
                                                (stepRow, stepIndex) => {
                                                  const stepLabel =
                                                    stepRow?.step ??
                                                    stepIndex + 1;
                                                  const stepMarksValue =
                                                    getFiniteNumber(
                                                      stepRow?.marks
                                                    );
                                                  const stepMaxMarksValue =
                                                    getFiniteNumber(
                                                      stepRow?.maxMarks
                                                    );
                                                  const stepMarksDisplay =
                                                    formatMarks(
                                                      stepMarksValue
                                                    ) ??
                                                    (typeof stepMarksValue ===
                                                      "number" &&
                                                    Number.isFinite(
                                                      stepMarksValue
                                                    )
                                                      ? stepMarksValue.toString()
                                                      : "0");
                                                  const stepMaxDisplay =
                                                    formatMarks(
                                                      stepMaxMarksValue
                                                    ) ??
                                                    (typeof stepMaxMarksValue ===
                                                      "number" &&
                                                    Number.isFinite(
                                                      stepMaxMarksValue
                                                    )
                                                      ? stepMaxMarksValue.toString()
                                                      : "0");
                                                  const solutionText =
                                                    normalizeTextValue(
                                                      stepRow?.solution ||
                                                        stepRow?.feedback ||
                                                        ""
                                                    );
                                                  return (
                                                    <tr
                                                      key={`${questionId}-step-${stepLabel}-${stepIndex}`}
                                                      className="border-b border-gray-100 last:border-b-0"
                                                    >
                                                      <td className="py-2 px-3 font-medium text-gray-700 whitespace-nowrap">
                                                        {stepLabel}
                                                      </td>
                                                      <td className="py-2 px-3 text-gray-700">
                                                        <MathJax dynamic>
                                                          <div className="whitespace-pre-line leading-relaxed">
                                                            {solutionText ||
                                                              "-"}
                                                          </div>
                                                        </MathJax>
                                                      </td>
                                                      <td className="py-2 px-3 text-right font-semibold text-gray-800 whitespace-nowrap">
                                                        {stepMarksDisplay}/
                                                        {stepMaxDisplay}
                                                      </td>
                                                    </tr>
                                                  );
                                                }
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </MathJaxContext>
              ) : (
                <div className="text-center py-10">
                  <p className="text-gray-600">
                    No result data is available at this time.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratedWorksheets;
